import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AnnouncementModal from "./AnnouncementModal";
import { Announcement, getNextAnnouncement, sendAnnouncementEvent } from "api/Announcements";
import { claimLoginModal, loginModalHolder } from "helpers/loginModal";

/**
 * Shows the newest announcement meant for this person, once, at login
 * (one pop-up per login: helpers/loginModal). Waits a moment before
 * claiming the slot so critical Action Items win; tells the layout when it
 * has decided, so the Discount Reminder knows whether it may show.
 */
const CLAIM_DELAY_MS = 1200;

const LoginAnnouncement: React.FC<{ onDecided?: (showing: boolean) => void }> = ({ onDecided }) => {
  const navigate = useNavigate();
  const [item, setItem] = useState<Announcement | null>(null);
  const decided = useRef(false);

  const decide = (showing: boolean) => {
    if (decided.current) return;
    decided.current = true;
    onDecided?.(showing);
  };

  useEffect(() => {
    let live = true;
    let timer: any;
    // Something already had this session's pop-up (an announcement shown
    // earlier in the session included): nothing until the next login.
    if (loginModalHolder()) {
      decide(false);
      return;
    }
    getNextAnnouncement().then((a) => {
      if (!live) return;
      if (!a) return decide(false);
      timer = setTimeout(() => {
        if (!live) return;
        if (!claimLoginModal("announcement")) return decide(false);
        setItem(a);
        decide(true);
        void sendAnnouncementEvent(a.id, "shown");
      }, CLAIM_DELAY_MS);
    });
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line

  if (!item) return null;
  const done = (type: "later" | "acknowledged" | "clicked" | "closed") => {
    void sendAnnouncementEvent(item.id, type);
    setItem(null);
  };
  return (
    <AnnouncementModal
      announcement={item}
      onReachedEnd={() => void sendAnnouncementEvent(item.id, "reached_end")}
      onLater={() => done("later")}
      onGotIt={() => done("acknowledged")}
      onClose={() => done("closed")}
      onAction={() => {
        const path = item.actionPath;
        done("clicked");
        if (path) navigate(path);
      }}
      onLink={(href) => {
        done("clicked");
        navigate(href);
      }}
    />
  );
};

export default LoginAnnouncement;
